import React from "react";
import { connect } from "react-redux";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Layout from "antd/es/layout";
import AntDMenu from "antd/es/menu";
import { useIntl } from "react-intl";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";

import PermissionName from "@mapmycustomers/shared/enum/PermissionName";
import CumulDashboard from "@mapmycustomers/shared/types/cumul/CumulDashboard";
import User from "@mapmycustomers/shared/types/User";

import { isStackRankAvailable } from "@app/scene/reports/store/selectors";
import ReportCategory, {
  getCategoryMessagesDisplayName,
} from "@app/scene/reports/utils/getCategoryMessagesDisplayName";
import useMenu from "@app/scene/reports/utils/useMenu";
import { getCumulDashboardList, getPinnedDashboards } from "@app/store/cumul";
import {
  hasPermission,
  isCumulEnabled,
  isCumulPromotionalUIVisited,
  isCurrentUserManager,
  isCurrentUserOwner,
  isCurrentUserReadOnly,
} from "@app/store/iam";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";

import styles from "./Menu.module.scss";

interface Props {
  canReadExports?: boolean;
  isCumulEnabled?: boolean;
  isCumulPromotionalUIVisited?: boolean;
  isOwnerOrManager?: boolean;
  isReadOnlyMember?: boolean;
  isStackRankAvailable?: boolean;
  pinnedDashboards?: CumulDashboard[];
  users?: User[];
}

const Menu: React.FC<Props> = ({
  canReadExports,
  isCumulEnabled,
  isCumulPromotionalUIVisited,
  isOwnerOrManager,
  isReadOnlyMember,
  isStackRankAvailable,
  pinnedDashboards,
  users,
}) => {
  const intl = useIntl();

  const defaultOpenKey =
    isCumulEnabled ||
    (isOwnerOrManager && !isCumulPromotionalUIVisited && users && users?.length > 10)
      ? getCategoryMessagesDisplayName(intl, ReportCategory.CUSTOM)
      : getCategoryMessagesDisplayName(intl, ReportCategory.GENERAL);

  const menuCategories = useMenu(
    intl,
    users,
    isOwnerOrManager,
    isReadOnlyMember,
    isStackRankAvailable,
    isCumulEnabled,
    pinnedDashboards,
    canReadExports
  );
  const { pathname } = useLocation();

  return (
    <Layout.Sider width={"250px"}>
      <AntDMenu
        className={styles.menu}
        defaultOpenKeys={[defaultOpenKey]}
        defaultSelectedKeys={[pathname]}
        mode="inline"
        theme="light"
      >
        {menuCategories.map((category) =>
          category?.path ? (
            <AntDMenu.Item
              className={styles.subTitle}
              icon={<FontAwesomeIcon className={styles.subtitleIcon} icon={category.icon} />}
              key={category.name}
            >
              <Link className={styles.link} to={category.path}>
                {category.name}
              </Link>
            </AntDMenu.Item>
          ) : (
            <AntDMenu.SubMenu
              className={styles.subTitle}
              icon={<FontAwesomeIcon className={styles.subtitleIcon} icon={category.icon} />}
              key={category.name}
              title={category.name}
            >
              {category.items
                ?.filter(({ path }) => !!path)
                .map(({ label, path }) => (
                  <AntDMenu.Item key={path!}>
                    <Link className={styles.link} to={path!}>
                      {label}
                    </Link>
                  </AntDMenu.Item>
                ))}
            </AntDMenu.SubMenu>
          )
        )}
      </AntDMenu>
    </Layout.Sider>
  );
};

const mapStateToProps = (state: RootState) => ({
  canReadExports: hasPermission(state)(PermissionName.EXPORT_READ),
  cumulDashboardList: getCumulDashboardList(state),
  isCumulEnabled: isCumulEnabled(state),
  isCumulPromotionalUIVisited: isCumulPromotionalUIVisited(state),
  isOwnerOrManager: isCurrentUserOwner(state) || isCurrentUserManager(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
  isStackRankAvailable: isStackRankAvailable(state),
  pinnedDashboards: getPinnedDashboards(state),
  users: getUsers(state),
});

export default connect(mapStateToProps, {})(Menu);
